<template>
  <div class="flex flex-col gap-6">
    <UnitFormSection title="Identificação">
      <div class="md:col-span-8">
        <GenericInput
          :disabled="disabled"
          :rules="[rules.form('name')]"
          width="100%"
          label="Nome"
          v-model="form.values.name"
          @validate="validate('name')"
          required
          data-testid="unit-name-input"
        />
      </div>

      <div class="md:col-span-4">
        <GenericInput
          :disabled="disabled"
          width="100%"
          label="CNPJ"
          required
          ref="cnpjField"
          :model-value="form.values.cnpj"
          @update:model-value="onCnpjInput"
          @validate="validate('cnpj')"
          :error="!!form.errors.cnpj"
          :error-messages="form.errors.cnpj"
          data-testid="unit-cnpj-input"
        />
      </div>

      <div class="md:col-span-6">
        <v-autocomplete
          :disabled="disabled"
          :model-value="companySelected"
          @update:model-value="emit('update:companySelected', $event)"
          class="w-full"
          color="primary20"
          label="* Empresa"
          :items="companies"
          variant="outlined"
          data-testid="unit-company-select"
        />
      </div>

      <div class="md:col-span-6">
        <v-select
          :disabled="disabled"
          :model-value="form.values.type"
          @update:model-value="onTypeChange"
          class="w-full"
          color="primary20"
          label="* Tipo de unidade"
          :items="HEALTH_UNIT_TYPE_OPTIONS"
          item-title="title"
          item-value="value"
          variant="outlined"
          :error-messages="form.errors.type || undefined"
          data-testid="unit-type-select"
        />
      </div>

      <div class="md:col-span-6">
        <OperationalGroupSelect
          :model-value="operationalGroup"
          @update:model-value="emit('update:operationalGroup', $event)"
          :items="operationalGroups"
          :disabled="disabled"
          label="* Grupo Operacional"
          density="default"
          :error="!!form.errors.operationalGroup"
          :error-messages="form.errors.operationalGroup"
          variant="outlined"
          data-testid="unit-operational-group-select"
        />
      </div>
    </UnitFormSection>

    <UnitFormSection title="Contato e responsáveis" hint="opcional">
      <div class="md:col-span-6">
        <GenericInput
          :disabled="disabled"
          label="Email"
          :rules="[rules.form('email')]"
          width="100%"
          v-model="form.values.email"
          @validate="validate('email')"
          data-testid="unit-email-input"
        />
      </div>

      <div class="md:col-span-6">
        <GenericInput
          :disabled="disabled"
          label="Telefone"
          width="100%"
          ref="phoneField"
          :model-value="form.values.mainPhone"
          @update:model-value="onPhoneInput"
          @validate="validate('mainPhone')"
          :error="!!form.errors.mainPhone"
          :error-messages="form.errors.mainPhone"
          data-testid="unit-phone-input"
        />
      </div>

      <div class="md:col-span-6">
        <GenericInput
          :disabled="disabled"
          label="Coordenação técnica"
          :rules="[rules.form('technicalManager')]"
          width="100%"
          v-model="form.values.technicalManager"
          @validate="validate('technicalManager')"
          data-testid="unit-technical-manager-input"
        />
      </div>

      <div class="md:col-span-6">
        <GenericInput
          :disabled="disabled"
          label="Gerência Geral"
          :rules="[rules.form('generalManager')]"
          width="100%"
          v-model="form.values.generalManager"
          @validate="validate('generalManager')"
          data-testid="unit-general-manager-input"
        />
      </div>
    </UnitFormSection>

    <UnitFormSection title="Informações adicionais" hint="opcional">
      <div class="md:col-span-4">
        <GenericInput
          :disabled="disabled"
          type="number"
          label="Total de funcionários"
          :rules="[rules.form('quantityColaborators')]"
          width="100%"
          v-model="form.values.quantityColaborators"
          @validate="validate('quantityColaborators')"
          data-testid="unit-employees-input"
        />
      </div>

      <div class="md:col-span-8">
        <GenericInput
          :disabled="disabled"
          label="Perfil"
          :rules="[rules.form('profile')]"
          width="100%"
          v-model="form.values.profile"
          @validate="validate('profile')"
          data-testid="unit-profile-input"
        />
      </div>

      <div class="md:col-span-12">
        <GenericInput
          :disabled="disabled"
          label="Link do Waze"
          :rules="[rules.form('linkWaze')]"
          width="100%"
          v-model="form.values.linkWaze"
          @validate="validate('linkWaze')"
          data-testid="unit-waze-input"
        />
      </div>
    </UnitFormSection>
  </div>
</template>

<script setup lang="ts">
import { ref } from 'vue'
import GenericInput from '@/components/designSystem/Inputs/GenericInput.vue'
import OperationalGroupSelect from '@/components/designSystem/Select/OperationalGroupSelect.vue'
import type { SelectOption } from '@/types'
import type { OperationalGroup } from '@/Network/Types/Responses/ScheduleUnitResponses'
import { injectHealthUnitForm } from '../../composables/useHealthUnitForm'
import { maskPhoneInput } from '../../helpers/phone'
import { maskCnpjInput } from '../../helpers/cnpj'
import { syncMaskedInput } from '../../helpers/maskedInput'
import type { MaskedField } from '../../helpers/maskedInput'
import { HEALTH_UNIT_TYPE_OPTIONS, HealthUnitType } from '../../unitType'
import UnitFormSection from './UnitFormSection.vue'

defineProps<{
  disabled: boolean
  companies: SelectOption[]
  operationalGroups: OperationalGroup[]
  companySelected?: number
  operationalGroup: OperationalGroup | null
}>()

const emit = defineEmits<{
  (e: 'update:companySelected', value: number | undefined): void
  (e: 'update:operationalGroup', value: OperationalGroup | null): void
}>()

const { form, validate, rules } = injectHealthUnitForm()

const phoneField = ref<MaskedField>(null)
const cnpjField = ref<MaskedField>(null)

function onPhoneInput(value: string | number | undefined): void {
  const masked = maskPhoneInput(value)
  form.values.mainPhone = masked
  syncMaskedInput(phoneField, masked)
}

/** O tipo define o preset de raio sugerido no modal de localização (PR seguinte). */
function onTypeChange(value: HealthUnitType | null): void {
  form.values.type = value ?? undefined
  validate('type')
}

function onCnpjInput(value: string | number | undefined): void {
  const masked = maskCnpjInput(value)
  form.values.cnpj = masked
  syncMaskedInput(cnpjField, masked)
}
</script>
